/**
 * Range — practice shot logger + auto-diagnose.
 *
 * Data model:
 *   - ShotSession  = one range trip (one bow setup + arrow build) at one distance.
 *     Currently stores aggregate group size + a pattern descriptor (quick mode).
 *     Later (v83): adds per-shot {x,y} coords for the SVG target plot mode.
 *   - RangeState   = the whole log + derived stats (PBs, streaks, achievements).
 *
 * Storage: localStorage now ('sparrow.range.v1'). Customer-metafield sync
 * after subscription billing goes live so PBs persist across devices.
 */

/**
 * Discrete group patterns the user picks from in quick-log mode. Each pattern
 * triggers a different set of tune-wizard recommendations + form tips.
 *
 *   centered      — Tight group, no directional bias. Tune is dialed.
 *   left-string   — Group landing left of center (RH shooter). Cam lean +
 *                   grip torque + center-shot left of spec are the suspects.
 *   right-string  — Inverse of left-string. Often spine too weak or rest IN.
 *   high          — Group landing high consistently. Nock high or rest low.
 *   low           — Group low. Nock low or rest high. Less common.
 *   vertical      — Vertical stringing — arrows scattered up/down same X.
 *                   Classic spine inconsistency or rest contact.
 *   horizontal    — Horizontal stringing left↔right same Y. Form / grip /
 *                   release torque.
 *   scattered     — No discernible pattern, wide spread. Usually form or
 *                   broken-in strings (or not enough arrows to tell).
 *   diagonal      — Diagonal line through group. Combination of horizontal
 *                   + vertical issues; fix vertical first.
 */
export type GroupPattern =
  | 'centered'
  | 'left-string'
  | 'right-string'
  | 'high'
  | 'low'
  | 'vertical'
  | 'horizontal'
  | 'scattered'
  | 'diagonal';

export const GROUP_PATTERN_LABELS: Record<GroupPattern, string> = {
  'centered':     '✓ Centered (tight, no bias)',
  'left-string':  '← Stringing LEFT of center',
  'right-string': '→ Stringing RIGHT of center',
  'high':         '↑ Stringing HIGH',
  'low':          '↓ Stringing LOW',
  'vertical':     '↕ Vertical string (up/down same X)',
  'horizontal':   '↔ Horizontal string (left/right same Y)',
  'scattered':    '✗ Scattered (wide, no pattern)',
  'diagonal':     '⤢ Diagonal string (combo of issues)',
};

/**
 * A single logged range session. In quick mode the per-shot positions are
 * empty; in per-shot mode (v83) we'll populate `shots: ShotCoord[]` instead
 * of (or alongside) the aggregate `groupSizeIn` field.
 */
export interface ShotSession {
  /** Stable client-generated id (uuid-ish). */
  id: string;
  /** ISO 8601 datetime of the session. */
  loggedAt: string;
  /** Setup id this session was shot with (matches a Setup in useSetups). Optional —
   *  user can log a session before creating a setup. */
  setupId?: string;
  /** Distance in yards (typical: 20, 30, 40, 50, 60, 70, 80, 100). */
  distanceYd: number;
  /** Number of arrows in the group. Required so we can normalize by count. */
  arrowCount: number;
  /** Group size — outer-to-outer in inches (quick mode). */
  groupSizeIn: number;
  /** What the group LOOKED like. Drives auto-diagnose. */
  pattern: GroupPattern;
  /** Optional notes the user typed. Surfaced in history view. */
  notes?: string;
  /** Per-shot coordinates (v83+, per-shot mode). Inches from center. */
  shots?: Array<{ x: number; y: number }>;
  // ─── v130 (Range Log "dialed in") additive extensions ───
  // All optional — v1 sessions remain valid. See range/v2-schema.ts for full
  // shapes. Keep these here so the existing storage shim doesn't need any
  // type-cast hatches.
  /** Per-shot rich coordinate + metadata. Live sessions populate this. */
  richShots?: import('./v2-schema').RichShot[];
  /** Session-level environmental conditions. */
  conditions?: import('./v2-schema').SessionConditions;
  /** Immutable setup snapshot captured at session start. */
  setupSnapshot?: import('./v2-schema').SessionSetupSnapshot;
  /** Range / location name. */
  location?: string;
  /** ISO timestamp the session ended (vs `loggedAt` = started). */
  endedAt?: string;
  // ─── v132 (Range Log Advanced) additive extensions ───
  /** Ordered list of sets (one set = one configuration). Optional —
   *  sessions without `sets` are treated as a single implicit set wrapping
   *  the flat `richShots[]`. See `migrate.ts`. */
  sets?: import('./v3-schema').ShotSet[];
  /** Stamp written on save when the session reaches the v3 shape. */
  schemaVersion?: 3;
}

/** Personal best per distance bucket. */
export interface PersonalBest {
  distanceYd: number;
  /** Smallest group size logged at this distance. */
  groupSizeIn: number;
  /** Session id that set the PB — link back to it from the history view. */
  sessionId: string;
  /** When the PB was set. */
  achievedAt: string;
  /** Arrow count of that session (PB only counts for ≥4-arrow groups). */
  arrowCount: number;
}

/** Streak + tally state computed from the log. */
export interface RangeStats {
  /** Total sessions logged ever. */
  totalSessions: number;
  /** Total arrows logged ever. */
  totalArrows: number;
  /** Sessions logged this week (rolling 7 days). */
  sessionsThisWeek: number;
  /** Sessions logged this month (rolling 30 days). */
  sessionsThisMonth: number;
  /** Days-in-a-row with at least one session. Resets when a day is skipped. */
  currentStreakDays: number;
  /** Best streak ever. */
  longestStreakDays: number;
  /** Achievements earned (badge ids — see ACHIEVEMENTS list). */
  achievements: string[];
}

/** Recommendation surfaced after a session is logged. */
export interface DiagnoseRec {
  /** Short title — shown as the card header. */
  title: string;
  /** Why this fix matters in one sentence. */
  why: string;
  /** Step-by-step. Free of jargon when possible. */
  fix: string;
  /**
   * Where to send the user next. Either a Tune wizard step id (deep-link
   * into the wizard) or null (just informational).
   */
  goToTuneStep?:
    | 'preflight'
    | 'bow_grip'
    | 'cam_timing'
    | 'tiller'
    | 'center_shot'
    | 'nocking_d_loop'
    | 'paper_tune'
    | 'bareshaft'
    | 'walk_back'
    | 'broadhead_group';
  /** Severity hint — drives card color (info / warning / critical). */
  severity: 'info' | 'warn' | 'critical';
}

/** All persisted Range data. */
export interface RangeState {
  /** Schema version — bump when shape changes so we can migrate. */
  version: 1;
  /** All logged sessions, newest first. */
  sessions: ShotSession[];
  /** PBs keyed by distance string ('20', '30', '40'...). */
  personalBests: Record<string, PersonalBest>;
}

// ─────────────────────────────────────────────────────────────────────────
// Achievements catalog — light gamification. Earned automatically based on
// session log + computed stats. Display in profile + share later.
// ─────────────────────────────────────────────────────────────────────────

export interface Achievement {
  id: string;
  title: string;
  description: string;
  /** Tier: 'bronze' | 'silver' | 'gold' — drives badge color. */
  tier: 'bronze' | 'silver' | 'gold';
  /** Check function — receives full state, returns true if earned. */
  test: (state: RangeState, stats: RangeStats) => boolean;
}

export const ACHIEVEMENTS: readonly Achievement[] = [
  {
    id: 'first-log',
    title: 'First Log',
    description: 'Log your first range session.',
    tier: 'bronze',
    test: (_, s) => s.totalSessions >= 1,
  },
  {
    id: 'arrows-100',
    title: 'Centurion',
    description: 'Log 100 arrows total.',
    tier: 'bronze',
    test: (_, s) => s.totalArrows >= 100,
  },
  {
    id: 'arrows-1000',
    title: 'Four-Figure Quiver',
    description: 'Log 1,000 arrows total.',
    tier: 'silver',
    test: (_, s) => s.totalArrows >= 1000,
  },
  {
    id: 'arrows-10000',
    title: 'Iron Bow',
    description: 'Log 10,000 arrows total — you live for this.',
    tier: 'gold',
    test: (_, s) => s.totalArrows >= 10000,
  },
  {
    id: 'streak-7',
    title: 'Weekly Habit',
    description: 'Log a session 7 days in a row.',
    tier: 'bronze',
    test: (_, s) => s.longestStreakDays >= 7,
  },
  {
    id: 'streak-30',
    title: 'Monthly Devotion',
    description: '30-day logging streak. Most people quit at 12.',
    tier: 'silver',
    test: (_, s) => s.longestStreakDays >= 30,
  },
  {
    id: 'streak-100',
    title: 'Sparrow Daily',
    description: '100 days in a row. You ARE the bench.',
    tier: 'gold',
    test: (_, s) => s.longestStreakDays >= 100,
  },
  {
    id: 'pb-sub2-40',
    title: 'Sub-2 at 40',
    description: 'A 2" or tighter group at 40 yards (≥4 arrows).',
    tier: 'silver',
    test: (state) => {
      const pb = state.personalBests['40'];
      return !!pb && pb.groupSizeIn <= 2.0 && pb.arrowCount >= 4;
    },
  },
  {
    id: 'pb-sub3-60',
    title: 'Sub-3 at 60',
    description: 'A 3" or tighter group at 60 yards (≥4 arrows).',
    tier: 'silver',
    test: (state) => {
      const pb = state.personalBests['60'];
      return !!pb && pb.groupSizeIn <= 3.0 && pb.arrowCount >= 4;
    },
  },
  {
    id: 'pb-sub5-80',
    title: 'Sub-5 at 80',
    description: 'A 5" or tighter group at 80 yards (≥4 arrows).',
    tier: 'gold',
    test: (state) => {
      const pb = state.personalBests['80'];
      return !!pb && pb.groupSizeIn <= 5.0 && pb.arrowCount >= 4;
    },
  },
  {
    id: 'long-range-100',
    title: 'Triple Digits',
    description: 'Log a session at 100 yards or beyond.',
    tier: 'silver',
    test: (state) => state.sessions.some((s) => s.distanceYd >= 100),
  },
] as const;
